<template lang="pug">
	.audio-tracker-range-selection-ui(v-if="tracker.rangeSelection.visible")
		.selection-range(v-if="hasSelection", :style="styleInfo")
			.edit-zone.left(@mousedown.stop="onEditZoneMouseDown(-1)")
				.top-elem
					.flex-row.align-center.center
						svg(height="15px" width="15px")
							polyline(points="0,0 15,0 0,15")
			.edit-zone.right(@mousedown.stop="onEditZoneMouseDown(1)")
				.top-elem.flex-row.align-center.center
					svg(height="15px" width="15px")
						polyline(points="0,0 15,0 16,15")
</template>

<script>
	export default {
		translationCategory: 'audioTracker.AudioTracker',
		name: 'audio-tracker',
		props: ['tracker'],
		data() {
			return {
				movingSide: null
			}
		},
		computed: {
			hasSelection() {
				return this.tracker.rangeSelection.start !== null && this.tracker.rangeSelection.end !== null && this.tracker.rangeSelection.start !== this.tracker.rangeSelection.end;
			},
			pps() {
				return this.tracker?.display?.pixelsPerSecond || 0;
			},
			range() {
				return [this.tracker.rangeSelection.start * this.pps, this.tracker.rangeSelection.end * this.pps].sort((a,b) => a-b);
			},
			styleInfo() {
				return {
					'--left': `${this.range[0]}px`,
					'--width': `${this.range[1] - this.range[0]}px`,
				}
			}
		},
		watch: {
			styleInfo() {
				this.updateRulerRangeSelection();
			},
			'tracker.rangeSelection.visible'() {
				this.updateRulerRangeSelection();
			}
		},
		mounted() {
			if (__DEBUG__)
				window.trackerRangeSelectionUI = this;
			
			this.$nextTick(() => {
				document.body.addEventListener('mousemove', this.onMouseMove);
				document.body.addEventListener('mouseup', this.onMouseUp);
				this.tracker.$refs.navigator?.addEventListener('mousedown', this.onMouseDown);
				this.tracker.$refs.ruler?.$el.addEventListener('mousedown', this.onMouseDown);

				this.updateRulerRangeSelection();
			});

			this.$eventsBus.$on('tracker-ruler-range-selection-click', this.onRulerRangeSelectionClick);
		},
		destroyed() {
			document.body.removeEventListener('mousemove', this.onMouseMove);
			document.body.removeEventListener('mouseup', this.onMouseUp);
			this.tracker.$refs.navigator?.removeEventListener('mousedown', this.onMouseDown);
			this.tracker.$refs.ruler?.$el.removeEventListener('mousedown', this.onMouseDown);
			this.$eventsBus.$off('tracker-ruler-range-selection-click', this.onRulerRangeSelectionClick);
		},
		methods: {
			updateRulerRangeSelection() {
				if (this.tracker.$refs.rulerRangeSelection) {
					Object.keys(this.styleInfo).map(k => {
						this.tracker.$refs.rulerRangeSelection.style.setProperty(k, this.styleInfo[k]);
					});
				}
			},
			onRulerRangeSelectionClick() {
				this.movingSide = 'both';
			},
			onEditZoneMouseDown(side) {
				this.movingSide = (this.tracker.rangeSelection.start < this.tracker.rangeSelection.end ? 1 : -1) * side === -1 ? 'left' : 'right';
			},
			onMouseDown(e) {
				if (__DEBUG__)
					console.debug('onMouseDown', { e });
				if (this.tracker.rangeSelection.visible) {
					this.movingSide = 'right';
					this.$eventsBus.$emit('tracker-range-selection-change', {
						type: 'value',
						position: 'start',
						value: e.layerX / this.pps
					});
					this.$eventsBus.$emit('tracker-range-selection-change', {
						type: 'value',
						position: 'end',
						value: e.layerX / this.pps
					});
				}
			},
			onMouseUp() {
				if (this.tracker.rangeSelection.visible) {
					this.movingSide = null;

					if (this.tracker.rangeSelection.start === this.tracker.rangeSelection.end) {
						this.tracker.rangeSelection.start = null;
						this.tracker.rangeSelection.end = null;
					}
				}
			},
			onMouseMove(e) {
				if (this.movingSide) {
					if (this.movingSide === 'both') {
						this.$eventsBus.$emit('tracker-range-selection-change', { 
							type: 'delta',
							position: 'start',
							value: e.movementX / this.pps
						});
						this.$eventsBus.$emit('tracker-range-selection-change', { 
							type: 'delta',
							position: 'end',
							value: e.movementX / this.pps
						});
					} else {
						this.$eventsBus.$emit('tracker-range-selection-change', { 
							type: 'delta',
							position: this.movingSide === 'left' ? 'start' : 'end',
							value: e.movementX / this.pps
						});
					}
				}
			}
		}
	};
</script>
<style lang='scss'>	
	@import "main/themes/generated/variables.base.scss";

	$border-color: #aaa;
	$border-bg: #33333322;

	.audio-tracker-range-selection-ui {
		pointer-events: none;
		position: absolute;
		left: 0px;
		top: 0px;
		right: 0px;
		bottom: 0px;
		z-index: 10;

		.selection-range {
			position: absolute;
			left: var(--left);
			width: var(--width);
			height: 100%;
			background: $border-bg;
			background: repeating-linear-gradient(
				-45deg,
				#ffffff55,
				#ffffff55 30px,
				#ffffff33 30px,
				#ffffff33 60px,
			);
		}

		.edit-zone {
			position: absolute;
			top: 0px;
			height: 100%;
			cursor: w-resize;
			pointer-events: all;
			transition: 0.1s;
			
			svg {
				fill: $border-color;
				transition: 0.1s;
			}

			&.left {
				left: 0px;
				border-left: 1px solid $border-color;
			}

			&.right {
				right: 0px;
				border-right: 1px solid $border-color;
			}

			&:hover {
				svg {
					fill: $base-accent;
				}

				&.left {
					border-left: 1px solid $base-accent;
				}

				&.right {
					border-right: 1px solid $base-accent;
				}
			}
		}
	}

	.tracker--ruler-range-selection {
		height: 100%;
		background: $base-accent;
		position: absolute;
		left: var(--left);
		width: var(--width);
		opacity: 0.2;
		
		&:hover {
			cursor: w-resize;
			opacity: 0.5;
		}
	}
</style>
